import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Sparkles, MessageSquare, ChevronRight } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../services/api';
import { useToast } from '../common/Toast';
import ProjectIntelligenceWorkspaceShell from './ProjectIntelligenceWorkspaceShell';
import { getPiWorkspacePath } from './projectIntelligencePhases';
import '../../styles/dpr-premium.css';

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'clean'],
  ],
};

export default function DprWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [regeneratingIndex, setRegeneratingIndex] = useState(null);

  useEffect(() => {
    if (id) fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/sourcing/project-requirements/${id}`);
      if (res.data.success) {
        const p = res.data.data;
        setProject(p);
        setSections(p.dprSections || []);
      } else {
        setProject(null);
      }
    } catch (err) {
      error('Failed to load project');
      setProject(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (sections.length === 0) return;
    try {
      setSaving(true);
      await api.put(`/sourcing/project-requirements/${id}/dpr-builder`, { sections });
    } catch (err) {
      console.error('Failed to auto-save manual edits', err);
    } finally {
      setSaving(false);
    }
  };

  const handleContentChange = (index, value) => {
    const newSections = [...sections];
    newSections[index].content = value;
    setSections(newSections);
  };

  const cleanJsonString = (str) => {
    const clean = str.replace(/```json/gi, '').replace(/```/g, '').trim();
    return JSON.parse(clean);
  };

  const handleGenerateAI = async () => {
    if (!project) return;
    setGenerating(true);
    try {
      const prompt = `
        You are an expert ${project.projectType || 'procurement'} consultant drafting a Detailed Project Report (DPR).
        Project Title: ${project.projectTitle}
        Type: ${project.projectType || project.category}
        Estimated Value: ${project.estimatedValue}
        Location: ${project.location}
        Problem Statement: ${project.problemStatement}
        Contributions/Notes: ${project.contributions?.map(c => {
        if (c.type === 'text') return `Note: ${c.content}`;
        if (c.extractedText) return `Document (${c.fileName}):\n${c.extractedText}`;
        return `Attached Document: ${c.fileName}`;
      }).join('\n\n')}
        
        Generate exactly 6 sections for the DPR:
        1. Scope of Work
        2. Bill of Quantities (BOQ) Summary
        3. Implementation Timeline
        4. Site Details
        5. Risk Assessment (Identify specific execution risks based on the context, such as data migration, change management, etc.)
        6. Cost & Timeline Summary (Estimate duration and budget ranges based strictly on the source documents)

        CRITICAL INSTRUCTIONS:
        - Base your response ENTIRELY on the provided Contributions/Notes and Attached Files. Do not hallucinate generic IT or civil engineering standards.
        - You MUST extract and preserve exact metrics and requirements from the source documents. 
        - If a document mentions specific constraints like "4,200 contracts", "₹47L", "AES-256", "TLS 1.3", "Sunday maintenance", or specific vendors/tools (e.g., DocuSign, Leegality), you MUST include those exact details. 
        - DO NOT use "TBD", generic percentages, or "Undefined" if the source provides actual numbers or estimates. Be highly specific.
        
        Return the result STRICTLY as a JSON array of objects. 
        Each object MUST have:
        - "title": The section name
        - "content": Detailed html formatted content (use <p>, <ul>, <li>, <strong>)
        - "aiFlag": Any AI warning/suggestion (e.g. "Needs monsoon buffer"). Leave empty string if none.
        - "status": Set to "Needs review" if there is an aiFlag, otherwise "AI drafted".
      `;

      const filePaths = project.contributions
        ?.filter(c => c.type !== 'text' && c.content)
        .map(c => c.content)
        .filter(Boolean) || [];

      const res = await api.post('/ai/gemini', {
        message: prompt,
        files: filePaths,
        maxTokens: 16000,
      });

      const responseText = res.data?.response || '';

      if (res.data && responseText) {
        const parsedSections = cleanJsonString(responseText);

        if (!parsedSections || parsedSections.length === 0) {
          error('AI returned no sections. Check console logs.');
          return;
        }

        setSections(parsedSections);
        success('DPR Draft Generated Successfully!');
        await api.put(`/sourcing/project-requirements/${id}/dpr-builder`, { sections: parsedSections });
      }
    } catch (err) {
      error('Failed to generate AI draft');
      console.error('[DPR-Builder] Generation failed:', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async (index) => {
    setRegeneratingIndex(index);
    try {
      const section = sections[index];
      const prompt = `
        You are regenerating a specific section of a Detailed Project Report.
        Project Title: ${project.projectTitle}
        Section Title: ${section.title}
        Current Content: ${section.content}
        AI Flag / Instruction: Please update the content to resolve this flag: "${section.aiFlag}" or improve the section based on best practices.
        
        Return ONLY a JSON object representing the updated section with:
        - "title": ${section.title}
        - "content": The newly generated HTML content
        - "aiFlag": "" (empty string since it's resolved)
        - "status": "AI drafted"
      `;

      const res = await api.post('/ai/gemini', { message: prompt });
      if (res.data && res.data.response) {
        const updatedSection = cleanJsonString(res.data.response);
        const newSections = [...sections];
        newSections[index] = updatedSection;
        setSections(newSections);
        success('Section regenerated successfully');
        await api.put(`/sourcing/project-requirements/${id}/dpr-builder`, { sections: newSections });
      }
    } catch (err) {
      error('Regeneration failed');
    } finally {
      setRegeneratingIndex(null);
    }
  };

  const headerActions = project
    ? [
        {
          label: saving ? 'Saving...' : 'Save Draft',
          icon: <Save size={18} />,
          variant: 'outline',
          onClick: handleSave,
          disabled: saving || sections.length === 0,
        },
        {
          label: 'Proceed to Approvals',
          icon: <ChevronRight size={18} />,
          variant: 'primary',
          onClick: () => navigate(getPiWorkspacePath('regulatory', id)),
        },
      ]
    : [];

  const needsReviewCount = sections.filter(s => s.status === 'Needs review').length;

  return (
    <ProjectIntelligenceWorkspaceShell
      phase="builder"
      projectId={id}
      projectTitle={project?.projectTitle}
      loading={loading}
      notFound={!loading && !project}
      actions={headerActions}
    >
      {sections.length === 0 ? (
        <div className="d-flex flex-column align-items-center justify-content-center py-5">
          <div className="pi-card text-center p-5" style={{ maxWidth: 560 }}>
            <Sparkles size={48} className="text-primary mb-3" />
            <h3 className="fw-bold">Ready to draft the DPR?</h3>
            <p className="text-muted mb-4">
              Generate a 6-section Detailed Project Report from your project brief, structured fields, and team contributions.
            </p>
            <button
              type="button"
              className="btn btn-primary btn-lg px-5"
              onClick={handleGenerateAI}
              disabled={generating}
            >
              {generating ? 'AI is drafting your DPR...' : 'Generate AI Draft'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="pi-alert-banner pi-alert-banner--success mb-4 d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div className="d-flex align-items-start gap-3">
              <Sparkles className="mt-1 flex-shrink-0 text-success" size={24} />
              <div>
                <div className="fw-bold">
                  Generation complete — seeded from project brief ({project?.contributions?.length || 0} contributions)
                </div>
                <div className="small mt-1 text-muted">
                  {sections.length} sections drafted.
                  {needsReviewCount > 0 && (
                    <span className="fw-semibold text-warning-emphasis">
                      {' '}
                      {needsReviewCount} flagged for review.
                    </span>
                  )}{' '}
                  All sections are editable inline.
                </div>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleGenerateAI}
              disabled={generating}
            >
              {generating ? 'Regenerating...' : 'Regenerate All'}
            </button>
          </div>

          <div className="d-flex flex-column gap-4 pb-4">
            {sections.map((section, index) => (
              <div key={index} className="pi-section-card">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold m-0">
                    {index + 1}. {section.title}
                  </h6>
                  <span
                    className={`pi-badge ${
                      section.status === 'Needs review' ? 'pi-badge--warning' : 'pi-badge--success'
                    }`}
                  >
                    {section.status}
                  </span>
                </div>

                <div className="mb-3 dpr-quill-wrapper" onBlur={handleSave}>
                  <ReactQuill
                    theme="snow"
                    value={section.content}
                    onChange={(value) => handleContentChange(index, value)}
                    modules={modules}
                    className="border-0"
                  />
                </div>

                {section.aiFlag && (
                  <div className="pi-alert-banner mb-3 d-flex gap-3 align-items-start">
                    <Sparkles size={18} className="text-warning flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-warning-emphasis">AI suggestion:</strong>
                      <span className="ms-2">{section.aiFlag}</span>
                    </div>
                  </div>
                )}

                <div className="d-flex gap-3 align-items-center pt-2 border-top">
                  <button
                    type="button"
                    className={`btn btn-sm ${section.status === 'Needs review' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => handleRegenerate(index)}
                    disabled={regeneratingIndex === index}
                  >
                    {regeneratingIndex === index ? 'Regenerating...' : 'Regenerate'}
                  </button>
                  <button type="button" className="btn btn-sm btn-link text-muted text-decoration-none d-flex align-items-center gap-1">
                    <MessageSquare size={16} /> Comment
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </ProjectIntelligenceWorkspaceShell>
  );
}
